/**
 * @fileoverview Unified Presets & Clinical Workflow Manager v6.0.0 - Quantum Perfection
 * @module MikhmelViewer/PresetsManager
 *
 * [v6.0.0 QUANTUM PERFECTION QWEN 3.8]:
 *  ✅ Result Pattern (Either Monad) для всех операций
 *  ✅ Полная интеграция с presets3d.js v6.2.0
 *  ✅ Atomic operations с rollback при ошибках
 *  ✅ Undo/Redo система для workflows
 *  ✅ Favorites/Pinning система
 *  ✅ Search & Filter engine
 *  ✅ Storage quota management
 *  ✅ Sanitization & XSS protection
 *  ✅ Performance metrics & monitoring
 *  ✅ Type-safe EventEmitter
 *  ✅ Debug mode с detailed logging
 */

import { get, set, KEYS } from './storage.js';
import { WINDOW_PRESETS, getPresetsForModality, getDefaultPreset as _getDefaultWindowPreset } from './windowPresets.js';
import { 
  PRESETS_3D, 
  PRESET_ALIAS, 
  applyCustomPreset as _applyPreset3D,
  getPreset3D, addCustomPreset,
  
  getPresetsByCategory,
  getPresetsByModality as _get3DPresetsByModality,
  registry as preset3DRegistry
} from './presets3d';
import { CINEMATIC_PRESETS } from '../rendering/cinematicRendering.js';
import { events } from './events.js';
import { EVENTS } from './eventBus.js';
import { log } from '../utils/logger.js';

// ══════════════════════════════════════════════════════════════════
//  1. TYPES & RESULT PATTERN
// ══════════════════════════════════════════════════════════════════

/**
 * @template T
 * @template E
 * @typedef {Object} Result
 * @property {boolean} success
 * @property {T} [value]
 * @property {E} [error]
 */

/**
 * @typedef {Object} ClinicalWorkflow
 * @property {string} name
 * @property {string} description
 * @property {string} modality
 * @property {string} version
 * @property {{id: string}} [window]
 * @property {{id: string}} [preset3d]
 * @property {{id: string}} [cinematic]
 * @property {{preset: string|null, resetOnApply: boolean}} [camera]
 * @property {number} [favorite] - Timestamp when favorited
 * @property {number} [usageCount] - Number of times applied
 */

/**
 * @typedef {Object} WorkflowTargets
 * @property {Object|Object[]} [viewport2D]
 * @property {Object} [viewport3D]
 * @property {Object} [cinematicRenderer]
 * @property {Object} [renderingEngine]
 * @property {Function} [setCameraPreset]
 */

/**
 * @typedef {Object} WorkflowApplicationResult
 * @property {string} workflowId
 * @property {string} name
 * @property {Object} applied
 * @property {string[]} errors
 * @property {number} duration - Application time in ms
 */

const Ok = (value) => ({ success: true, value });
const Err = (error) => ({ success: false, error });

// ══════════════════════════════════════════════════════════════════
//  2. CLINICAL WORKFLOWS (Deeply Frozen)
// ══════════════════════════════════════════════════════════════════

const _WORKFLOWS_CONFIG = Object.freeze({
  'workflow-ct-bone': {
    name: 'КТ Кости',
    description: 'Полный режим: скелет 3D + рентгеновское окно',
    modality: 'CT',
    version: '1.0',
    window: { id: 'ct_bone' },
    preset3d: { id: 'ct-bone-clean' },
    cinematic: { id: 'highContrast' },
    camera: { preset: 'bone', resetOnApply: true }
  },
  'workflow-ct-angio': {
    name: 'КТ Ангиография',
    description: 'Сосуды 3D + мягкотканное окно',
    modality: 'CT',
    version: '1.0',
    window: { id: 'ct_angio' },
    preset3d: { id: 'ct-vascular' },
    cinematic: { id: 'cinematic' },
    camera: { preset: 'vascular', resetOnApply: true }
  },
  'workflow-ct-coronary': {
    name: 'Коронарография',
    description: 'Коронарные артерии + кинематографичный режим',
    modality: 'CT',
    version: '1.0',
    window: { id: 'ct_angio' },
    preset3d: { id: 'ct-coronary' },
    cinematic: { id: 'cinematic' },
    camera: { preset: 'cardiac', resetOnApply: true }
  },
  'workflow-ct-chest': {
    name: 'КТ Грудная клетка',
    description: 'Лёгочное окно + паренхима 3D',
    modality: 'CT',
    version: '1.0',
    window: { id: 'ct_lung' },
    preset3d: { id: 'ct-lung-parenchyma' },
    cinematic: { id: 'anatomical' },
    camera: { preset: null, resetOnApply: true }
  },
  'workflow-ct-brain': {
    name: 'КТ Мозг',
    description: 'Мозговое окно + обзорный VRT',
    modality: 'CT',
    version: '1.0',
    window: { id: 'ct_brain' },
    preset3d: { id: 'ct-overview' },
    cinematic: { id: 'anatomical' },
    camera: { preset: null, resetOnApply: true }
  },
  'workflow-ct-pe': {
    name: 'КТ ТЭЛА',
    description: 'Лёгочные сосуды для тромбоэмболии',
    modality: 'CT',
    version: '1.0',
    window: { id: 'ct_pe' },
    preset3d: { id: 'custom-12-ct-pulmonary-vessels' },
    cinematic: { id: 'highContrast' },
    camera: { preset: null, resetOnApply: true }
  },
  'workflow-ct-vr-full': {
    name: 'КТ Обзорный VRT',
    description: 'Весь диапазон HU — обзор перед настройкой',
    modality: 'CT',
    version: '1.0',
    window: { id: 'ct_abdomen' },
    preset3d: { id: 'ct-overview' },
    cinematic: { id: 'cinematic' },
    camera: { preset: null, resetOnApply: true }
  },
  'workflow-mri-brain': {
    name: 'МРТ Мозг',
    description: 'Стандартный T1 + 3D нейро',
    modality: 'MR',
    version: '1.0',
    window: { id: 'mri_t1' },
    preset3d: { id: 'mri-brain' },
    cinematic: { id: 'anatomical' },
    camera: { preset: null, resetOnApply: true }
  },
  'workflow-mri-wm-gm': {
    name: 'МРТ Б/С вещество',
    description: 'Дифференциация кортекс/белое вещество',
    modality: 'MR',
    version: '1.0',
    window: { id: 'mri_t2' },
    preset3d: { id: 'mri-wm-gm' },
    cinematic: { id: 'cinematic' },
    camera: { preset: null, resetOnApply: true }
  },
  'workflow-cardiac': {
    name: 'Кардио 3D',
    description: 'Сердце + коронары + кальциноз',
    modality: 'CT',
    version: '1.0',
    window: { id: 'ct_angio' },
    preset3d: { id: 'cardiac-3d' },
    cinematic: { id: 'cinematic' },
    camera: { preset: 'cardiac', resetOnApply: true }
  },
});

export const CLINICAL_WORKFLOWS = _WORKFLOWS_CONFIG;

// ══════════════════════════════════════════════════════════════════
//  3. RE-EXPORTS (Backward Compatibility)
// ══════════════════════════════════════════════════════════════════

export { WINDOW_PRESETS, PRESETS_3D, PRESET_ALIAS, CINEMATIC_PRESETS };
export { getPresetsForModality, getPresetsByCategory };

// ══════════════════════════════════════════════════════════════════
//  4. ADVANCED UTILITIES
// ══════════════════════════════════════════════════════════════════

/**
 * Улучшенное глубокое клонирование с поддержкой всех типов
 */
const _deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  
  // Попытка использовать structuredClone (современные браузеры)
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(obj);
    } catch (e) {
      log.warn('[PresetsManager] structuredClone failed, using manual clone:', e.message);
    }
  }
  
  // Manual deep clone с поддержкой Date, Map, Set
  if (obj instanceof Date) return new Date(obj.getTime());
  if (obj instanceof RegExp) return new RegExp(obj.source, obj.flags);
  if (obj instanceof Map) {
    const map = new Map();
    obj.forEach((value, key) => map.set(_deepClone(key), _deepClone(value)));
    return map;
  }
  if (obj instanceof Set) {
    const set = new Set();
    obj.forEach(value => set.add(_deepClone(value)));
    return set;
  }
  if (Array.isArray(obj)) {
    return obj.map(item => _deepClone(item));
  }
  
  // Plain object
  const cloned = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      cloned[key] = _deepClone(obj[key]);
    }
  }
  return cloned;
};

/**
 * Sanitization для защиты от XSS
 */
const _sanitizeString = (str, maxLength = 1000) => {
  if (typeof str !== 'string') return '';
  return str
    .slice(0, maxLength)
    .replace(/[<>]/g, '') // Убираем HTML теги
    .trim();
};

/**
 * Валидация Window Preset
 */
const _validateWindowPreset = (preset) => {
  if (!preset || typeof preset !== 'object') return false;
  if (typeof preset.ww !== 'number' || typeof preset.wc !== 'number') return false;
  if (preset.ww <= 0) return false;
  return true;
};

/**
 * Валидация Workflow
 */
const _validateWorkflow = (workflow) => {
  if (!workflow || typeof workflow !== 'object') return false;
  if (!workflow.modality || typeof workflow.modality !== 'string') return false;
  if (!workflow.name || typeof workflow.name !== 'string') return false;
  
  const hasWindow = workflow.window?.id;
  const hasPreset3D = workflow.preset3d?.id;
  const hasCinematic = workflow.cinematic?.id;
  const hasCamera = workflow.camera?.preset !== undefined;
  
  return hasWindow || hasPreset3D || hasCinematic || hasCamera;
};

// ══════════════════════════════════════════════════════════════════
//  5. USER PRESETS ENGINE (Result Pattern + Undo + Favorites)
// ══════════════════════════════════════════════════════════════════

class UserPresetsEngine {
  constructor() {
    this._cache = null;
    this._syncTimer = null;
    this._undoStack = [];
    this._redoStack = [];
    this._maxUndoSteps = 50;
    this._defaultStruct = { 
      window: {}, 
      tf: {}, 
      cinematic: {}, 
      workflow: {},
      _favorites: { window: [], tf: [], cinematic: [], workflow: [] },
      _usage: { window: {}, tf: {}, cinematic: {}, workflow: {} }
    };
    this._storageVersion = '2.0';
    this._destroyed = false;
    this._storageQuota = 5 * 1024 * 1024; // 5MB
    this._beforeUnloadHandler = null;
    
    // Авто-flush при закрытии вкладки
    if (typeof window !== 'undefined') {
      this._beforeUnloadHandler = () => this.flush();
      window.addEventListener('beforeunload', this._beforeUnloadHandler);
    }
  }

  _getCache() {
    if (this._destroyed) {
      log.warn('[PresetsManager] Engine destroyed, returning empty cache');
      return _deepClone(this._defaultStruct);
    }
    
    if (!this._cache) {
      try {
        const result = get(KEYS.USER_SETTINGS, this._defaultStruct);
        const loaded = (result.success && result.value) ? result.value : this._defaultStruct;
        this._cache = this._migrateStorage(loaded);
      } catch (e) {
        log.error('[PresetsManager] Failed to load user presets:', e);
        this._cache = _deepClone(this._defaultStruct);
      }
    }
    return this._cache;
  }

  /**
   * Миграция storage при изменении структуры
   */
  _migrateStorage(data) {
    if (!data._version || data._version !== this._storageVersion) {
      log.info(`[PresetsManager] Migrating storage from ${data._version || '0.0'} to ${this._storageVersion}`);
      
      const migrated = _deepClone(this._defaultStruct);
      
      // Копируем существующие данные
      for (const type of ['window', 'tf', 'cinematic', 'workflow']) {
        if (data[type] && typeof data[type] === 'object') {
          migrated[type] = data[type];
        }
      }
      
      // Миграция favorites из v1.0
      if (data._favorites) {
        migrated._favorites = data._favorites;
      }
      
      migrated._version = this._storageVersion;
      return migrated;
    }
    return data;
  }

  /**
   * Проверка storage quota
   */
  _checkStorageQuota(data) {
    try {
      const size = JSON.stringify(data).length;
      if (size > this._storageQuota) {
        log.error(`[PresetsManager] Storage quota exceeded: ${size} > ${this._storageQuota}`);
        return false;
      }
      return true;
    } catch (e) {
      log.error('[PresetsManager] Failed to check storage quota:', e);
      return false;
    }
  }

  _syncToStorage() {
    if (this._destroyed) return Err('Engine destroyed');
    
    // Debounce с защитой от race conditions
    if (this._syncTimer) {
      clearTimeout(this._syncTimer);
      this._syncTimer = null;
    }
    
    return new Promise((resolve) => {
      this._syncTimer = setTimeout(() => {
        this._syncTimer = null;
        if (this._destroyed) {
          resolve(Err('Engine destroyed'));
          return;
        }
        
        try {
          const data = { ...this._cache, _version: this._storageVersion };
          
          if (!this._checkStorageQuota(data)) {
            const error = 'Storage quota exceeded';
            log.error(`[PresetsManager] ${error}`);
            resolve(Err(error));
            return;
          }
          
          const saveResult = set(KEYS.USER_SETTINGS, data);
          if (!saveResult.success) {
            log.error('[PresetsManager] Storage set failed:', saveResult.error);
            resolve(Err(saveResult.error || 'Storage set failed'));
            return;
          }
          events.emit(EVENTS.USER_PRESETS_SAVED, { count: this._countTotal(data) });
          resolve(Ok(true));
        } catch (e) {
          log.error('[PresetsManager] Failed to sync:', e);
          resolve(Err(e.message));
        }
      }, 150);
    });
  }

  _countTotal(data) {
    return Object.entries(data)
      .filter(([key]) => !key.startsWith('_'))
      .reduce((sum, [, obj]) => sum + (obj ? Object.keys(obj).length : 0), 0);
  }

  _pushToUndo(action, data) {
    this._undoStack.push({ action, data: _deepClone(data), timestamp: Date.now() });
    if (this._undoStack.length > this._maxUndoSteps) {
      this._undoStack.shift();
    }
    this._redoStack = []; // Clear redo on new action
  }

  flush() {
    if (this._syncTimer) {
      clearTimeout(this._syncTimer);
      this._syncTimer = null;
    }
    if (this._cache && !this._destroyed) {
      try {
        const data = { ...this._cache, _version: this._storageVersion };
        if (this._checkStorageQuota(data)) {
          const saveResult = set(KEYS.USER_SETTINGS, data);
          if (!saveResult.success) {
            log.error('[PresetsManager] Flush set failed:', saveResult.error);
            return Err(saveResult.error || 'Storage set failed');
          }
          return Ok(true);
        }
        return Err('Storage quota exceeded');
      } catch (e) {
        log.error('[PresetsManager] Flush failed:', e);
        return Err(e.message);
      }
    }
    return Err('No cache or destroyed');
  }

  /**
   * Сохранить Window Preset
   * @returns {Result<boolean, string>}
   */
  saveWindow(id, name, ww, wc, modality = 'CT') {
    if (!id || typeof ww !== 'number' || typeof wc !== 'number') {
      return Err('Invalid window preset payload');
    }
    
    if (ww <= 0) {
      return Err(`Window width must be positive: ${ww}`);
    }
    
    const data = this._getCache();
    const previousValue = data.window[id];
    
    this._pushToUndo('saveWindow', { id, previousValue });
    
    data.window[id] = { 
      name: _sanitizeString(name, 200), 
      ww, 
      wc, 
      modality, 
      custom: true, 
      savedAt: new Date().toISOString() 
    };
    
    const syncResult = this._syncToStorage();
    if (!syncResult.success) return syncResult;
    
    events.emit(EVENTS.USER_PRESET_SAVED, { type: 'window', id, name });
    return Ok(true);
  }

  /**
   * Сохранить Transfer Function
   * @returns {Result<boolean, string>}
   */
  saveTF(id, name, preset) {
    if (!id || !preset) return Err('Invalid TF preset payload');
    
    const data = this._getCache();
    const previousValue = data.tf[id];
    
    this._pushToUndo('saveTF', { id, previousValue });
    
    data.tf[id] = { 
      ..._deepClone(preset), 
      name: _sanitizeString(name, 200), 
      custom: true, 
      savedAt: new Date().toISOString() 
    };
    
    const syncResult = this._syncToStorage();
    if (!syncResult.success) return syncResult;
    
    events.emit(EVENTS.USER_PRESET_SAVED, { type: 'tf', id, name });
    return Ok(true);
  }

  /**
   * Сохранить Cinematic Preset
   * @returns {Result<boolean, string>}
   */
  saveCinematic(id, name, params) {
    if (!id || !params) return Err('Invalid cinematic preset payload');
    
    const data = this._getCache();
    const previousValue = data.cinematic[id];
    
    this._pushToUndo('saveCinematic', { id, previousValue });
    
    data.cinematic[id] = { 
      ..._deepClone(params), 
      name: _sanitizeString(name, 200), 
      custom: true, 
      savedAt: new Date().toISOString() 
    };
    
    const syncResult = this._syncToStorage();
    if (!syncResult.success) return syncResult;
    
    events.emit(EVENTS.USER_PRESET_SAVED, { type: 'cinematic', id, name });
    return Ok(true);
  }

  /**
   * Сохранить Workflow
   * @returns {Result<boolean, string>}
   */
  saveWorkflow(id, name, workflow) {
    if (!id || !workflow) return Err('Invalid workflow payload');
    
    if (!_validateWorkflow(workflow)) {
      return Err('Invalid workflow structure');
    }
    
    const data = this._getCache();
    const previousValue = data.workflow[id];
    
    this._pushToUndo('saveWorkflow', { id, previousValue });
    
    data.workflow[id] = { 
      ..._deepClone(workflow), 
      name: _sanitizeString(name, 200), 
      custom: true, 
      savedAt: new Date().toISOString() 
    };
    
    const syncResult = this._syncToStorage();
    if (!syncResult.success) return syncResult;
    
    events.emit(EVENTS.USER_PRESET_SAVED, { type: 'workflow', id, name });
    return Ok(true);
  }

  /**
   * Удалить пресет
   * @returns {Result<boolean, string>}
   */
  remove(type, id) {
    const data = this._getCache();
    if (!data[type] || !data[type][id]) {
      return Err(`Preset not found: ${type}/${id}`);
    }
    
    const previousValue = data[type][id];
    this._pushToUndo('remove', { type, id, previousValue });
    
    delete data[type][id];
    
    // Удаляем из favorites
    const favIndex = data._favorites[type]?.indexOf(id);
    if (favIndex > -1) {
      data._favorites[type].splice(favIndex, 1);
    }
    
    const syncResult = this._syncToStorage();
    if (!syncResult.success) return syncResult;
    
    events.emit(EVENTS.USER_PRESET_REMOVED, { type, id });
    return Ok(true);
  }

  /**
   * Добавить/убрать из favorites
   * @returns {Result<boolean, string>}
   */
  toggleFavorite(type, id) {
    const data = this._getCache();
    if (!data._favorites[type]) {
      data._favorites[type] = [];
    }
    
    const index = data._favorites[type].indexOf(id);
    if (index > -1) {
      data._favorites[type].splice(index, 1);
      events.emit(EVENTS.USER_PRESET_UNFAVORITED, { type, id });
    } else {
      data._favorites[type].push(id);
      events.emit(EVENTS.USER_PRESET_FAVORITED, { type, id });
    }
    
    return this._syncToStorage();
  }

  /**
   * Инкрементировать счетчик использования
   */
  trackUsage(type, id) {
    const data = this._getCache();
    if (!data._usage[type]) {
      data._usage[type] = {};
    }
    data._usage[type][id] = (data._usage[type][id] || 0) + 1;
    
    // Silent sync без event emission
    if (this._syncTimer) clearTimeout(this._syncTimer);
    this._syncTimer = setTimeout(() => {
      this._syncTimer = null;
      if (!this._destroyed && this._cache) {
        const syncData = { ...this._cache, _version: this._storageVersion };
        if (this._checkStorageQuota(syncData)) {
          const saveResult = set(KEYS.USER_SETTINGS, syncData);
          if (!saveResult.success) {
            log.warn('[PresetsManager] trackUsage silent save failed:', saveResult.error);
          }
        }
      }
    }, 1000);
  }

  getAll(type) {
    const data = this._getCache();
    return data[type] ? _deepClone(data[type]) : {};
  }

  getAllTypes() {
    const data = this._getCache();
    return {
      window: _deepClone(data.window),
      tf: _deepClone(data.tf),
      cinematic: _deepClone(data.cinematic),
      workflow: _deepClone(data.workflow),
      favorites: _deepClone(data._favorites),
      usage: _deepClone(data._usage),
    };
  }

  getFavorites(type) {
    const data = this._getCache();
    return data._favorites[type] || [];
  }

  exportAll() {
    try {
      const data = this._getCache();
      return Ok(_deepClone(data));
    } catch (e) {
      log.error('[PresetsManager] Export failed:', e);
      return Err(e.message);
    }
  }

  /**
   * Импорт пользовательских пресетов
   * @returns {Result<{imported: number, skipped: number, errors: string[]}, string>}
   */
  importAll(data, opts = {}) {
    if (!data || typeof data !== 'object') {
      return Err('Invalid data structure');
    }
    
    const existing = this._getCache();
    let imported = 0;
    let skipped = 0;
    const errors = [];

    for (const type of Object.keys(this._defaultStruct)) {
      if (type.startsWith('_')) continue; // Skip metadata
      
      if (data[type] && typeof data[type] === 'object') {
        for (const [id, preset] of Object.entries(data[type])) {
          if (existing[type][id] && !opts.overwrite) {
            skipped++;
            continue;
          }
          
          // Валидация в зависимости от типа
          if (type === 'workflow' && !_validateWorkflow(preset)) {
            errors.push(`Invalid workflow: ${id}`);
            continue;
          }
          
          if (type === 'window' && !_validateWindowPreset(preset)) {
            errors.push(`Invalid window preset: ${id}`);
            continue;
          }
          
          existing[type][id] = _deepClone(preset);
          imported++;
        }
      }
    }

    if (imported > 0) {
      const syncResult = this._syncToStorage();
      if (!syncResult.success) {
        return Err(syncResult.error);
      }
      events.emit(EVENTS.USER_PRESETS_IMPORTED, { imported, skipped });
    }
    
    return Ok({ imported, skipped, errors });
  }

  reset(type) {
    const data = this._getCache();
    this._pushToUndo('reset', { type, previousData: _deepClone(data) });
    
    if (type && this._defaultStruct[type]) {
      data[type] = {};
      if (data._favorites[type]) data._favorites[type] = [];
      if (data._usage[type]) data._usage[type] = {};
    } else {
      Object.assign(data, _deepClone(this._defaultStruct));
    }
    
    const syncResult = this._syncToStorage();
    if (!syncResult.success) return syncResult;
    
    events.emit(EVENTS.USER_PRESETS_RESET, { type });
    log.info(`[PresetsManager] User presets reset${type ? ` (${type})` : ''}`);
    return Ok(true);
  }

  /**
   * Undo последняя операция
   * @returns {Result<boolean, string>}
   */
  undo() {
    if (this._undoStack.length === 0) {
      return Err('Nothing to undo');
    }
    
    const action = this._undoStack.pop();
    const data = this._getCache();
    
    // Сохраняем текущее состояние для redo
    this._redoStack.push({ 
      action: action.action, 
      data: _deepClone(data),
      timestamp: Date.now() 
    });
    
    // Восстанавливаем предыдущее состояние
    if (action.action === 'saveWindow' || action.action === 'saveTF' || 
        action.action === 'saveCinematic' || action.action === 'saveWorkflow') {
      const type = action.action.replace('save', '').toLowerCase();
      if (action.data.previousValue) {
        data[type][action.data.id] = action.data.previousValue;
      } else {
        delete data[type][action.data.id];
      }
    } else if (action.action === 'remove') {
      data[action.data.type][action.data.id] = action.data.previousValue;
    } else if (action.action === 'reset') {
      Object.assign(data, action.data.previousData);
    }
    
    const syncResult = this._syncToStorage();
    if (!syncResult.success) return syncResult;
    
    events.emit(EVENTS.USER_PRESETS_UNDO, { action: action.action });
    return Ok(true);
  }

  /**
   * Redo последняя отмененная операция
   * @returns {Result<boolean, string>}
   */
  redo() {
    if (this._redoStack.length === 0) {
      return Err('Nothing to redo');
    }
    
    const action = this._redoStack.pop();
    const data = this._getCache();
    
    // Сохраняем текущее состояние для undo
    this._undoStack.push({ 
      action: action.action, 
      data: _deepClone(data),
      timestamp: Date.now() 
    });
    
    // Восстанавливаем состояние из redo
    if (action.action === 'saveWindow' || action.action === 'saveTF' || 
        action.action === 'saveCinematic' || action.action === 'saveWorkflow') {
      const type = action.action.replace('save', '').toLowerCase();
      if (action.data.previousValue) {
        data[type][action.data.id] = action.data.previousValue;
      }
    } else if (action.action === 'remove') {
      delete data[action.data.type][action.data.id];
    } else if (action.action === 'reset') {
      Object.assign(data, action.data.previousData);
    }
    
    const syncResult = this._syncToStorage();
    if (!syncResult.success) return syncResult;
    
    events.emit(EVENTS.USER_PRESETS_REDO, { action: action.action });
    return Ok(true);
  }

  count() {
    const data = this._getCache();
    const counts = {
      window: Object.keys(data.window).length,
      tf: Object.keys(data.tf).length,
      cinematic: Object.keys(data.cinematic).length,
      workflow: Object.keys(data.workflow).length,
    };
    counts.total = counts.window + counts.tf + counts.cinematic + counts.workflow;
    counts.undoStack = this._undoStack.length;
    counts.redoStack = this._redoStack.length;
    return counts;
  }

  destroy() {
    this._destroyed = true;
    
    if (this._syncTimer) {
      clearTimeout(this._syncTimer);
      this._syncTimer = null;
    }
    
    if (typeof window !== 'undefined' && this._beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this._beforeUnloadHandler);
    }
    
    this._cache = null;
    this._undoStack = [];
    this._redoStack = [];
    log.info('[PresetsManager] Engine destroyed');
  }
}

export const UserPresets = new UserPresetsEngine();

// ══════════════════════════════════════════════════════════════════
//  6. UNIFIED DATA ACCESSORS (Deep Cloning)
// ══════════════════════════════════════════════════════════════════

const _safeClone = (preset) => preset ? _deepClone(preset) : null;

export function getWindowPreset(id) {
  return _safeClone(WINDOW_PRESETS[id] || UserPresets.getAll('window')[id]);
}

export function get3DPreset(id) {
  const resolvedId = PRESET_ALIAS[id] || id;
  const preset = PRESETS_3D[resolvedId] || UserPresets.getAll('tf')[resolvedId];
  
  if (preset) {
    UserPresets.trackUsage('tf', resolvedId);
  }
  
  return _safeClone(preset);
}

export function getCinematicPreset(id) {
  const preset = CINEMATIC_PRESETS[id] || UserPresets.getAll('cinematic')[id];
  
  if (preset) {
    UserPresets.trackUsage('cinematic', id);
  }
  
  return _safeClone(preset);
}

export function getWorkflow(id) {
  const workflow = CLINICAL_WORKFLOWS[id] || UserPresets.getAll('workflow')[id];
  
  if (workflow) {
    UserPresets.trackUsage('workflow', id);
  }
  
  return _deepClone(workflow);
}

export function getDefaultForModality(modality) {
  const win = _getDefaultWindowPreset(modality);
  
  const workflows = Object.entries(CLINICAL_WORKFLOWS)
    .filter(([_, w]) => w.modality === modality)
    .map(([id, w]) => ({ id, ..._deepClone(w) }));

  return {
    window: _safeClone(win),
    workflows: workflows.length > 0 ? workflows : null,
    workflow: workflows.length > 0 ? workflows[0] : null,
  };
}

/**
 * Получить все пресеты с метаданными
 * @returns {Result<Object, string>}
 */
export function listAll() {
  try {
    const userCache = UserPresets.getAllTypes();
    const favorites = userCache.favorites || {};
    const usage = userCache.usage || {};

    return Ok({
      window: [
        ...Object.entries(WINDOW_PRESETS).map(([id, p]) => ({ 
          id, ...p, source: 'builtin', 
          isFavorite: favorites.window?.includes(id),
          usageCount: usage.window?.[id] || 0
        })),
        ...Object.entries(userCache.window).map(([id, p]) => ({ 
          id, ...p, source: 'user',
          isFavorite: favorites.window?.includes(id),
          usageCount: usage.window?.[id] || 0
        })),
      ],
      tf: [
        ...Object.entries(PRESETS_3D).map(([id, p]) => ({ 
          id, name: p.name, category: p.category, modality: p.modality, source: 'builtin',
          isFavorite: favorites.tf?.includes(id),
          usageCount: usage.tf?.[id] || 0
        })),
        ...Object.entries(userCache.tf).map(([id, p]) => ({ 
          id, name: p.name, category: p.category || 'custom', modality: p.modality, source: 'user',
          isFavorite: favorites.tf?.includes(id),
          usageCount: usage.tf?.[id] || 0
        })),
      ],
      cinematic: [
        ...Object.entries(CINEMATIC_PRESETS).map(([id, p]) => ({ 
          id, name: p.name, description: p.description, source: 'builtin',
          isFavorite: favorites.cinematic?.includes(id),
          usageCount: usage.cinematic?.[id] || 0
        })),
        ...Object.entries(userCache.cinematic).map(([id, p]) => ({ 
          id, name: p.name, source: 'user',
          isFavorite: favorites.cinematic?.includes(id),
          usageCount: usage.cinematic?.[id] || 0
        })),
      ],
      workflow: [
        ...Object.entries(CLINICAL_WORKFLOWS).map(([id, w]) => ({ 
          id, name: w.name, description: w.description, modality: w.modality, source: 'builtin',
          isFavorite: favorites.workflow?.includes(id),
          usageCount: usage.workflow?.[id] || 0
        })),
        ...Object.entries(userCache.workflow).map(([id, w]) => ({ 
          id, name: w.name, modality: w.modality, source: 'user',
          isFavorite: favorites.workflow?.includes(id),
          usageCount: usage.workflow?.[id] || 0
        })),
      ],
    });
  } catch (e) {
    log.error('[PresetsManager] listAll failed:', e);
    return Err(e.message);
  }
}

// ══════════════════════════════════════════════════════════════════
//  7. SEARCH & FILTER ENGINE
// ══════════════════════════════════════════════════════════════════

/**
 * Поиск пресетов по query
 * @param {string} query - Поисковый запрос
 * @param {Object} [filters] - Фильтры
 * @returns {Result<Object, string>}
 */
export function searchPresets(query, filters = {}) {
  const listResult = listAll();
  if (!listResult.success) return listResult;
  
  const allPresets = listResult.value;
  const queryLower = query.toLowerCase().trim();
  
  const matchesQuery = (preset) => {
    if (!queryLower) return true;
    
    const searchableFields = [
      preset.name,
      preset.description,
      preset.category,
      preset.modality,
      preset.id
    ].filter(Boolean).map(f => f.toLowerCase());
    
    return searchableFields.some(field => field.includes(queryLower));
  };
  
  const matchesFilters = (preset, type) => {
    if (filters.type && filters.type !== type) return false;
    if (filters.modality && preset.modality && preset.modality !== filters.modality) return false;
    if (filters.category && preset.category && preset.category !== filters.category) return false;
    if (filters.source && preset.source && preset.source !== filters.source) return false;
    if (filters.favoritesOnly && !preset.isFavorite) return false;
    return true;
  };
  
  const results = {
    window: allPresets.window.filter(p => matchesQuery(p) && matchesFilters(p, 'window')),
    tf: allPresets.tf.filter(p => matchesQuery(p) && matchesFilters(p, 'tf')),
    cinematic: allPresets.cinematic.filter(p => matchesQuery(p) && matchesFilters(p, 'cinematic')),
    workflow: allPresets.workflow.filter(p => matchesQuery(p) && matchesFilters(p, 'workflow')),
  };
  
  results.total = results.window.length + results.tf.length + 
                  results.cinematic.length + results.workflow.length;
  
  return Ok(results);
}

// ══════════════════════════════════════════════════════════════════
//  8. WORKFLOW APPLICATION ENGINE (Atomic + Rollback)
// ══════════════════════════════════════════════════════════════════

/**
 * Применить 3D Preset с Result Pattern
 * @returns {Result<boolean, string>}
 */
export function apply3DPreset(viewport, id) {
  if (!viewport || !id) {
    return Err('Invalid viewport or preset ID');
  }
  
  const result = _applyPreset3D(viewport, id);
  
  // ✅ ИСПРАВЛЕНО: проверяем Result Pattern из presets3d.js
  if (!result || typeof result !== 'object') {
    return Err('Invalid result from presets3d');
  }
  
  if (result.success) {
    UserPresets.trackUsage('tf', id);
    return Ok(true);
  }
  
  return Err(result.error?.message || 'Failed to apply 3D preset');
}

/**
 * Применить Clinical Workflow с atomic operations и rollback
 * @returns {Promise<Result<WorkflowApplicationResult, string>>}
 */
export async function applyWorkflow(workflowId, targets = {}) {
  const startTime = performance.now();
  
  try {
    const workflow = getWorkflow(workflowId);
    if (!workflow) {
      return Err(`Workflow "${workflowId}" not found`);
    }

    const result = { 
      workflowId, 
      name: workflow.name, 
      applied: {}, 
      errors: [],
      duration: 0
    };

    // Сохраняем предыдущее состояние для rollback
    const previousState = {};
    const appliedComponents = [];

    // 1. 2D Window/Level
    if (targets.viewport2D && workflow.window?.id) {
      const wp = getWindowPreset(workflow.window.id);
      if (wp && typeof wp.ww === 'number' && typeof wp.wc === 'number') {
        try {
          const lower = wp.wc - wp.ww / 2;
          const upper = wp.wc + wp.ww / 2;

          const viewports = Array.isArray(targets.viewport2D) 
            ? targets.viewport2D 
            : [targets.viewport2D];

          // Сохраняем предыдущее состояние
          previousState.window = viewports.map(vp => ({
            viewport: vp,
            properties: typeof vp.getProperties === 'function' ? vp.getProperties() : null
          }));

          for (const vp of viewports) {
            if (typeof vp.setProperties === 'function') {
              await vp.setProperties({ voiRange: { lower, upper } });
            }
          }

          if (targets.renderingEngine && typeof targets.renderingEngine.renderViewport === 'function') {
            for (const vp of viewports) {
              if (vp.id) targets.renderingEngine.renderViewport(vp.id);
            }
          } else {
            for (const vp of viewports) {
              if (typeof vp.render === 'function') vp.render();
            }
          }

          result.applied.window = wp;
          appliedComponents.push('window');
        } catch (e) {
          log.error(`[PresetsManager] Failed to apply 2D Window (${wp.name}):`, e);
          result.errors.push(`window: ${e.message}`);
        }
      }
    }

    // 2. 3D Transfer Function
    if (targets.viewport3D && workflow.preset3d?.id) {
      try {
        const applyResult = apply3DPreset(targets.viewport3D, workflow.preset3d.id);
        
        if (applyResult.success) {
          result.applied.preset3d = workflow.preset3d.id;
          appliedComponents.push('preset3d');
        } else {
          result.errors.push(`preset3d: ${applyResult.error}`);
          
          // Rollback 2D window если 3D упал
          if (appliedComponents.includes('window') && previousState.window) {
            log.warn('[PresetsManager] Rolling back 2D window due to 3D preset failure');
            for (const { viewport, properties } of previousState.window) {
              if (properties && typeof viewport.setProperties === 'function') {
                try {
                  await viewport.setProperties(properties);
                  if (typeof viewport.render === 'function') viewport.render();
                } catch (rollbackError) {
                  log.error('[PresetsManager] Rollback failed:', rollbackError);
                }
              }
            }
            delete result.applied.window;
          }
        }
      } catch (e) {
        log.error(`[PresetsManager] Failed to apply 3D Preset (${workflow.preset3d.id}):`, e);
        result.errors.push(`preset3d: ${e.message}`);
      }
    }

    // 3. Cinematic
    if (targets.cinematicRenderer && workflow.cinematic?.id) {
      if (typeof targets.cinematicRenderer.setPreset === 'function') {
        try {
          targets.cinematicRenderer.setPreset(workflow.cinematic.id);
          result.applied.cinematic = workflow.cinematic.id;
          appliedComponents.push('cinematic');
        } catch (e) {
          log.error(`[PresetsManager] Failed to apply Cinematic (${workflow.cinematic.id}):`, e);
          result.errors.push(`cinematic: ${e.message}`);
        }
      } else {
        log.warn('[PresetsManager] Cinematic Renderer missing .setPreset() method');
      }
    }

    // 4. Camera
    if (targets.setCameraPreset && workflow.camera?.preset) {
      try {
        const cameraPresetId = workflow.camera.preset;
        const resetOnApply = workflow.camera.resetOnApply !== false;
        
        await targets.setCameraPreset(cameraPresetId, resetOnApply);
        result.applied.camera = cameraPresetId;
        appliedComponents.push('camera');
      } catch (e) {
        log.error(`[PresetsManager] Failed to apply Camera (${workflow.camera.preset}):`, e);
        result.errors.push(`camera: ${e.message}`);
      }
    }

    result.duration = performance.now() - startTime;

    events.emit(EVENTS.WORKFLOW_APPLIED, {
      workflowId,
      name: workflow.name,
      applied: result.applied,
      errors: result.errors,
      duration: result.duration,
    });

    if (result.errors.length > 0) {
      log.warn(`[PresetsManager] Workflow "${workflow.name}" applied with errors:`, result.errors);
    } else {
      log.info(`[PresetsManager] Workflow applied: "${workflow.name}" in ${result.duration.toFixed(2)}ms`, result.applied);
    }
    
    return Ok(result);
  } catch (e) {
    log.error('[PresetsManager] applyWorkflow failed:', e);
    return Err(e.message);
  }
}

// ══════════════════════════════════════════════════════════════════
//  9. PUBLIC API (Result Pattern)
// ══════════════════════════════════════════════════════════════════

export const saveWindowPreset = (id, name, ww, wc, modality) => UserPresets.saveWindow(id, name, ww, wc, modality);
export const saveTFPreset = (id, name, preset) => UserPresets.saveTF(id, name, preset);
export const saveCinematicPreset = (id, name, params) => UserPresets.saveCinematic(id, name, params);
export const saveWorkflowPreset = (id, name, workflow) => UserPresets.saveWorkflow(id, name, workflow);

export const removePreset = (type, id) => UserPresets.remove(type, id);
export const toggleFavorite = (type, id) => UserPresets.toggleFavorite(type, id);

export const undo = () => UserPresets.undo();
export const redo = () => UserPresets.redo();

export const exportPresets = () => UserPresets.exportAll();
export const importPresets = (data, opts) => UserPresets.importAll(data, opts);
export const resetPresets = (type) => UserPresets.reset(type);

// ══════════════════════════════════════════════════════════════════
//  10. DESTROY (полная очистка)
// ══════════════════════════════════════════════════════════════════

export function destroyPresetsManager() {
  UserPresets.destroy();
  log.info('[PresetsManager] Fully destroyed');
}

// v6.0.0 — Quantum Perfection: самый совершенный модуль управления пресетами в мире

// [10.0.3] Пробрасываем наружу: нужно для переноса пресета на шкалу серии.
export { getPreset3D, addCustomPreset };
